import {
  AchAccountType,
  type BankAccount,
  type BankAccountDetail,
  BankAccountStatus,
  BankAccountType,
  type CreateAchOrderInput,
  type CreateAchOrderInputV2,
  type CreateBankInternalTransferInput,
  type CreateBankInvestmentTransfer,
  type CreateBankInvestmentTransferInput,
  type CreateInvestmentAchTransferInput,
  type CustomerDetails,
  type InvestmentAccount,
  type InvestmentAccountAttributes,
  type InvestmentAccountDetail,
  InvestmentAccountStatus,
  InvestmentAccountType,
  LinkedAccountStatus,
  type LinkedAchAccount,
  MarketStatus,
  type Money,
  OrderType,
  type TransferAccountInput,
  type WealthOverview,
} from '@egw/api/src/models'
import { Cookies } from '@egw/clients'
import { configClient } from '@egw/config'
import { DEFAULT_CURRENCY_CODE, PARTY_ID_COOKIE } from '@egw/constants'
import { type TFunction, useTranslation } from '@egw/i18n'
import { addCentsToCurrency, buildBasicCurrency, Currency, getCurrencySymbol, removeCurrencySymbols } from '@egw/utils'
import * as yup from 'yup'
// import { useAppContext } from 'context/AppContextProvider'
import { sendEvent } from '@egw/events'
import { type Config } from '../../config/config'
import { AMOUNT_KEY, nullTransferFieldOptionv2, TRANSFERABLE_ACCOUNTS_DICTIONARY_APEX_MIGRATION } from '../../constants'
import {
  type AddCentsInterface,
  BankInvestmentTransferType,
  EBackendError,
  ETransferType,
  type TBackendError,
  type TBuildInvestmentAchPayload,
  type TBuildPayload,
  type TGetDisplayValueFromField,
  type TransferAccounts,
  TransferField,
  type TransferListOption,
  TransferSuccessTypes,
  type TSetMinAmountError,
} from '../../types'

export interface IGQLErrorResponse {
  response: {
    errors?: BackendError[]
  }
}

export interface BackendError {
  message: string
  locations: string[]
  extensions: {
    evergreenErrorType: string
    errorType: string
    origin: string
  }
}

// TODO remove this and delegate the hiding of the errors the components
// These error types are not shown to the user as messages, only as error state on the input
export const HIDDEN_ERROR_MESSAGES = [
  'required',
  'nullable',
  'length',
  'email',
  'testUSPhoneNumber',
  'testDateOfBirth',
  'testSSN',
]

// Extract error list from BE error object which can be in different formats
function getBeErrorList(errorResponse: unknown): BackendError[] | undefined {
  let errors: BackendError[] | undefined
  if ((errorResponse as IGQLErrorResponse).response.errors?.length) {
    errors = (errorResponse as IGQLErrorResponse).response.errors
  } else if (typeof (errorResponse as { message: string }).message === 'string') {
    try {
      errors = JSON.parse((errorResponse as { message: string }).message)
    } catch (e) {}
  }

  if (errors?.length) {
    return errors
  }
}

const privateAccountMask = (value?: string) => {
  if (!value) return ''
  return `x-${value}`
}

// DISPLAY VALUE ===================================================
export const useGetDisplayValue = ({ fieldOption, allFieldOptions, isModalOpen, t }: TGetDisplayValueFromField) => {
  let fieldDisplayValue = fieldOption?.value
    ? (allFieldOptions.find((option) => option.value === fieldOption.value)?.display.name as string)
    : null

  // Show Select if the From Input is empty and its Modal selection is open
  if (isModalOpen && fieldDisplayValue === null) {
    fieldDisplayValue = t('transfer.select')
  }

  return fieldDisplayValue
}

// DATA VALIDATORS ===================================================
export const checkIfExternalAccountsExist = (data: CustomerDetails): boolean => {
  if (!data) return false

  return Boolean(data.customer.linkedAccounts.achAccounts.length)
}

const checkIfOptionIsNullTransferOption = (option: TransferListOption) =>
  !option || option === nullTransferFieldOptionv2

export const checkIfAchAccount = (accountType: TransferAccounts) => {
  if (accountType === AchAccountType.CHECKING || accountType === AchAccountType.SAVING) {
    return true
  }

  return false
}

export const checkIfAchAccountPosition = (option: TransferListOption) => {
  if (checkIfOptionIsNullTransferOption(option)) return false

  return checkIfAchAccount(option.accountType)
}

export const checkIfBankAccount = (accountType: TransferAccounts) => {
  if (
    accountType === BankAccountType.HIGH_YIELD_CHECKING ||
    accountType === BankAccountType.HIGH_YIELD_SAVINGS ||
    accountType === BankAccountType.LIQUID_TREASURIES
  ) {
    return true
  }

  return false
}

export const checkIfBankAccountPosition = (option: TransferListOption) => {
  if (checkIfOptionIsNullTransferOption(option)) return false

  return checkIfBankAccount(option.accountType)
}

export const checkIfInvestmentAccount = (accountType: TransferAccounts | undefined): boolean => {
  if (accountType === undefined) return false

  const investmentAccountTypes: Partial<TransferAccounts>[] = [
    InvestmentAccountType.BROKERAGE,
    InvestmentAccountType.ROTH_IRA,
    InvestmentAccountType.TRADITIONAL_IRA,
    InvestmentAccountType.ROTH_IRA_INHERITED,
    InvestmentAccountType.TRADITIONAL_IRA_INHERITED,
    InvestmentAccountType.JOINT_BROKERAGE_TIC,
    InvestmentAccountType.JOINT_BROKERAGE_JTWROS,
    InvestmentAccountType.JOINT_BROKERAGE_TBTE,
    InvestmentAccountType.JOINT_BROKERAGE_CP,
    // COMPANION ACCOUNTS NOT HANDLED HERE
  ]

  return investmentAccountTypes.includes(accountType)
}

export const checkIfInvestmentAccountPosition = (option: TransferListOption) => {
  if (checkIfOptionIsNullTransferOption(option)) return false

  return checkIfInvestmentAccount(option.accountType)
}

// HANDLERS ============================================================
export const handleGenericBackendErrors = ({ backendError }: TBackendError): EBackendError => {
  const { t } = useTranslation('features-transfer')
  const errors = getBeErrorList(backendError)
  let errorType = EBackendError.GENERIC

  errors?.forEach((error) => {
    if (error.extensions.evergreenErrorType === 'NSF') {
      errorType = EBackendError.NSF
    } else if (error.extensions.evergreenErrorType === 'VELOCITY_LIMIT_EXCEEDED') {
      errorType = EBackendError.VelocityLimitExceeded
    }
    sendEvent('eg_transferFunds_error', {
      error,
    })
  })

  return errorType
}

export const setMinAmountError = ({ t, setError, money }: TSetMinAmountError) => {
  setError(TransferField.Amount, {
    message: t('transfer.error.transfer_minimum', {
      currencyCode: getCurrencySymbol(money.currencyCode),
    }),
  })
}

// Add cents to amount if they are missing
export const addCents = ({ formMethods }: AddCentsInterface) => {
  const value = formMethods.getValues(AMOUNT_KEY)
  Boolean(value) && formMethods.setValue(AMOUNT_KEY, addCentsToCurrency(value))
}

// DATA FACTORIES ============================================================
export const buildLinkedAchAccountFieldOption = (account: LinkedAchAccount): TransferListOption => {
  const dictionary = TRANSFERABLE_ACCOUNTS_DICTIONARY_APEX_MIGRATION
  return {
    // Account Identifier used as the value here for  (Bank Transfer Accounts)
    value: account.identifier, // Should remain unique
    display: {
      name: account.name,
      subtitle: privateAccountMask(account.mask),
      linkedAccountStatus: account.linkedAccountStatus,
      achType: account.achType,
      isEvergreenAccount: false,
    },
    // Alias not used as the account identifier for an external account
    alias: account.alias,
    accountType: account.achType,
    transferableToAccounts: dictionary[account.achType],
    isDisabled: false,
    attributes: undefined,
    retirementContribution: undefined, // Roth and Traditional IRA Value Only
  }
}

export const buildLinkedAchAccounts = (accounts: LinkedAchAccount[] | undefined): TransferListOption[] => {
  if (!accounts) return []

  return accounts
    .filter((account) => Boolean(account.linkedAccountStatus !== LinkedAccountStatus.EXPIRED))
    .map((account) => buildLinkedAchAccountFieldOption(account))
}

export const buildAccountName = (account: BankAccount | InvestmentAccount): string => {
  // If statement to be removed once Investments has AccountName
  if (checkIfInvestmentAccount(account.accountType)) {
    return 'Personal Portfolio'
  } else if (checkIfBankAccount(account.accountType)) {
    return (account as BankAccount).accountName.replace('High Yield ', '')
  }
  return ''
}

export const buildEvergreenAccountField = (
  account: BankAccountDetail,
  productName: string | undefined,
  t: TFunction<'features-transfer'>,
): TransferListOption => {
  const dictionary = TRANSFERABLE_ACCOUNTS_DICTIONARY_APEX_MIGRATION
  const name = buildAccountName(account)
  const subtitle = productName ?? t(`bank_account_titles.${account.accountTitle}`)
  return {
    value: account.accountNumber, // Should Remain Unique
    display: {
      name,
      subtitle,
      isEvergreenAccount: true,
    },
    alias: account.alias,
    accountType: account.accountType,
    transferableToAccounts: dictionary[account.accountType],
    isDisabled: account.status === BankAccountStatus.CLOSED,
    attributes: 'attributes' in account ? account.attributes : undefined,
  }
}

export const buildInternalAccounts = (
  accounts: (BankAccountDetail | null)[] | null | undefined,
  productName: string | undefined,
  t: TFunction<'features-transfer'>,
): TransferListOption[] => {
  if (!accounts) return []

  return accounts
    .filter((account) => account !== null)
    .map((account) => buildEvergreenAccountField(account, productName, t))
}

// Helper function to determine account type from typename
export const getAccountTypeFromTypename = (typename: string): InvestmentAccountType => {
  switch (typename) {
    case 'InvestmentRothIRAAccount':
      return InvestmentAccountType.ROTH_IRA
    case 'InvestmentIRAAccount':
      return InvestmentAccountType.TRADITIONAL_IRA
    case 'InvestmentInheritedRothIRAAccount':
      return InvestmentAccountType.ROTH_IRA_INHERITED
    case 'InvestmentInheritedIRAAccount':
      return InvestmentAccountType.TRADITIONAL_IRA_INHERITED
    case 'InvestmentBrokerageCompanionAccount':
      return InvestmentAccountType.COMPANION_BROKERAGE
    case 'InvestmentIRACompanionAccount':
      return InvestmentAccountType.COMPANION_IRA
    case 'InvestmentRothIRACompanionAccount':
      return InvestmentAccountType.COMPANION_ROTH_IRA
    case 'InvestmentJointBrokerageTenantsInCommonAccount':
      return InvestmentAccountType.JOINT_BROKERAGE_TIC
    case 'InvestmentJointBrokerageTenantsWithRightOfSurvivorshipAccount':
      return InvestmentAccountType.JOINT_BROKERAGE_JTWROS
    case 'InvestmentJointBrokerageTenantsInCommonCompanionAccount':
      return InvestmentAccountType.COMPANION_JOINT_BROKERAGE_TIC
    case 'InvestmentJointBrokerageTenantsWithRightOfSurvivorshipCompanionAccount':
      return InvestmentAccountType.COMPANION_JOINT_BROKERAGE_JTWROS
    case 'InvestmentJointBrokerageTenantsByEntiretyAccount':
      return InvestmentAccountType.JOINT_BROKERAGE_TBTE
    case 'InvestmentJointBrokerageTenantsByEntiretyCompanionAccount':
      return InvestmentAccountType.COMPANION_JOINT_BROKERAGE_TBTE
    case 'InvestmentJointBrokerageCommunityPropertyAccount':
      return InvestmentAccountType.JOINT_BROKERAGE_CP
    case 'InvestmentJointBrokerageCommunityPropertyCompanionAccount':
      return InvestmentAccountType.COMPANION_JOINT_BROKERAGE_CP
    default:
      return InvestmentAccountType.BROKERAGE
  }
}

// Helper function to check if account is a companion account
const isCompanionAccountType = (accountType: InvestmentAccountType): boolean => {
  const companionTypes = [
    InvestmentAccountType.COMPANION_BROKERAGE,
    InvestmentAccountType.COMPANION_IRA,
    InvestmentAccountType.COMPANION_ROTH_IRA,
    InvestmentAccountType.COMPANION_JOINT_BROKERAGE_JTWROS,
    InvestmentAccountType.COMPANION_JOINT_BROKERAGE_TIC,
    InvestmentAccountType.COMPANION_JOINT_BROKERAGE_TBTE,
    InvestmentAccountType.COMPANION_JOINT_BROKERAGE_CP,
  ]
  return companionTypes.includes(accountType)
}

// Helper function to check if account is an IRA
const isIRAAccountType = (accountType: InvestmentAccountType): boolean => {
  const iraTypes = [InvestmentAccountType.ROTH_IRA, InvestmentAccountType.TRADITIONAL_IRA]
  return iraTypes.includes(accountType)
}

// Helper function to check if account is an inherited IRA
const isInheritedIRAAccountType = (accountType: InvestmentAccountType): boolean => {
  const inheritedIRATypes = [InvestmentAccountType.ROTH_IRA_INHERITED, InvestmentAccountType.TRADITIONAL_IRA_INHERITED]
  return inheritedIRATypes.includes(accountType)
}

// Helper function to build account subtitle
const buildAccountSubtitle = ({
  account,
  isCompanionAccount,
  disableIRATransfers,
  isIRA,
  t,
}: {
  account: InvestmentAccountDetail
  isCompanionAccount: boolean
  disableIRATransfers: boolean
  isIRA: boolean
  t: TFunction<'features-transfer'>
}): string => {
  if (isCompanionAccount || (disableIRATransfers && isIRA)) {
    return t('transfer.companion_account_subtitles')
  }
  return t(`transfer.investment_account_subtitles.${account.__typename}`)
}

// Helper function to determine if account should be disabled
const shouldDisableAccount = ({
  account,
  isCompanionAccount,
  disableIRATransfers,
  isIRA,
}: {
  account: InvestmentAccountDetail
  isCompanionAccount: boolean
  disableIRATransfers: boolean
  isIRA: boolean
}): boolean => {
  return account.status === InvestmentAccountStatus.CLOSED || isCompanionAccount || (disableIRATransfers && isIRA)
}

// Helper function to build a single transfer list option
const buildTransferListOption = ({
  account,
  t,
  disableIRATransfers,
}: {
  account: InvestmentAccountDetail
  t: TFunction<'features-transfer'>
  disableIRATransfers: boolean
}): TransferListOption => {
  const dictionary = TRANSFERABLE_ACCOUNTS_DICTIONARY_APEX_MIGRATION
  const accountType = getAccountTypeFromTypename(account.__typename)
  const isCompanionAccount = isCompanionAccountType(accountType)
  const isIRA = isIRAAccountType(accountType)
  const isInheritedIRA = isInheritedIRAAccountType(accountType)

  const subtitle = buildAccountSubtitle({
    account,
    isCompanionAccount,
    disableIRATransfers,
    isIRA,
    t,
  })
  const isDisabled = shouldDisableAccount({
    account,
    isCompanionAccount,
    disableIRATransfers,
    isIRA,
  })

  return {
    value: account.accountNumber, // Should Remain Unique
    display: {
      name: account.accountName,
      subtitle,
      isEvergreenAccount: true,
      // Hide inherited IRAs from internal transfer dropdowns (BR-12)
      // Hide companion accounts from internal transfer dropdowns
      isHidden: isInheritedIRA || isCompanionAccount,
    },
    alias: account.alias,
    accountType,
    transferableToAccounts: dictionary[accountType],
    isDisabled,
    attributes: 'attributes' in account ? account.attributes : undefined,
    retirementContribution: 'retirementContribution' in account ? account.retirementContribution : undefined, // Roth and Traditional IRA Value Only
  }
}

export const buildPersonalPortfolioAccount = (
  accounts: InvestmentAccountDetail[] | undefined,
  t: TFunction<'features-transfer'>,
  disableIRATransfers: boolean,
): TransferListOption[] => {
  if (!accounts) return []

  return (
    accounts
      .map((account: InvestmentAccountDetail) => buildTransferListOption({ account, t, disableIRATransfers }))
      // Filter out hidden accounts for internal transfers - inherited IRAs will be excluded
      .filter((account) => !account.display.isHidden)
  )
}

export const buildAllInternalOptions = (
  data: WealthOverview | undefined,
  t: TFunction<'features-transfer'>,
  disableIRATransfers: boolean,
): TransferListOption[] => {
  if (!data) return []
  const extractBrokerageAccounts = buildPersonalPortfolioAccount(
    data.personalPortfolio?.accounts,
    t,
    disableIRATransfers,
  )

  // Passing product name as undefined as High Yield Checking Accounts will derive subtitle from BankAccountTitles
  const extractHighYieldCheckingAccounts = buildInternalAccounts(data.highYieldChecking?.accounts, undefined, t)

  const extractHighYieldSavingsAccounts = buildInternalAccounts(
    data.highYieldSavings?.accounts,
    data.highYieldSavings?.productName,
    t,
  )

  return [...extractBrokerageAccounts, ...extractHighYieldCheckingAccounts, ...extractHighYieldSavingsAccounts].sort(
    (a, b) => {
      if (a.isDisabled && !b.isDisabled) return 1
      if (!a.isDisabled && b.isDisabled) return -1
      return 0
    },
  )
}

export const buildMoneyFromAmountField = (amount: string): Money => ({
  amount: removeCurrencySymbols(amount),
  currencyCode: DEFAULT_CURRENCY_CODE,
})

export const buildAchOrderV2Payload = ({ fromPosition, toPosition, amount }: TBuildPayload): CreateAchOrderInputV2 => {
  const config = configClient.getConfig() as Config
  const money = {
    amount: removeCurrencySymbols(amount),
    currencyCode: DEFAULT_CURRENCY_CODE,
  }

  const isFromEvergreen = fromPosition.display.isEvergreenAccount

  // Derive if Evergreen Account is Paying Out or Collecting
  const orderType = isFromEvergreen ? OrderType.PAYMENT_ORDER : OrderType.COLLECTION_ORDER

  // If Evergreen is Paying out the alias of External account will be in _TO_ position
  // If Evergreen is Receiving the alias of Externl account will be in the _FROM_ position
  const achAccountAlias = orderType === OrderType.PAYMENT_ORDER ? toPosition.alias : fromPosition.alias

  // If Evergreen is Paying out the alias of Evergreen Account will be in _FROM_ position
  // If Evergreen is Receiving the alias of Evergreen Account will be in the _TO_ position
  const accountAlias = orderType === OrderType.PAYMENT_ORDER ? fromPosition.alias : toPosition.alias

  const achAuthTermsArray = config.zendeskArticles.achAuthorization.split('/')
  const achAuthTermsId = `${achAuthTermsArray[achAuthTermsArray.length - 2]}/${achAuthTermsArray[achAuthTermsArray.length - 1]}`

  return {
    accountAlias,
    achAccountAlias,
    orderType,
    money,
    achAuthTerms: {
      id: achAuthTermsId,
      accepted: true,
    },
  }
}

export const buildBankInvestmentPayload = ({
  fromPosition,
  toPosition,
  amount,
  liquidateAccount,
}: TBuildPayload): CreateBankInvestmentTransferInput => {
  const config = configClient.getConfig() as Config

  const money = buildMoneyFromAmountField(amount)

  const bankAccountIsTo = checkIfBankAccountPosition(toPosition)

  const bankAccountIsFrom = checkIfBankAccountPosition(fromPosition)

  const investmentAccountIsTo = checkIfInvestmentAccountPosition(toPosition)

  const bankAccountAlias: TransferAccountInput = {
    alias: bankAccountIsTo ? toPosition.alias : fromPosition.alias,
    isTo: bankAccountIsTo,
  }

  const investmentAccountAlias: TransferAccountInput = {
    alias: investmentAccountIsTo ? toPosition.alias : fromPosition.alias,
    isTo: investmentAccountIsTo,
  }

  const isIRATransaction =
    (bankAccountIsTo &&
      (fromPosition.accountType === InvestmentAccountType.ROTH_IRA ||
        fromPosition.accountType === InvestmentAccountType.TRADITIONAL_IRA)) ||
    (bankAccountIsFrom &&
      (toPosition.accountType === InvestmentAccountType.ROTH_IRA ||
        toPosition.accountType === InvestmentAccountType.TRADITIONAL_IRA))

  const achAuthTermsArray = config.zendeskArticles.achAuthorization.split('/')
  const achAuthTermsId = `${achAuthTermsArray[achAuthTermsArray.length - 2]}/${achAuthTermsArray[achAuthTermsArray.length - 1]}`

  const disclosures = isIRATransaction
    ? [
        {
          id: achAuthTermsId,
          accepted: true,
        },
      ]
    : []

  return {
    bankAccountAlias,
    investmentAccountAlias,
    money,
    liquidateAccount,
    disclosures,
  }
}

export const buildInvestmentAchPayload = ({
  fromPosition,
  toPosition,
  amount,
}: TBuildInvestmentAchPayload): CreateInvestmentAchTransferInput => {
  const config = configClient.getConfig() as Config
  const money = buildMoneyFromAmountField(amount)

  const achAccountisTo = checkIfAchAccountPosition(toPosition)

  const investmentAccountisTo = checkIfInvestmentAccountPosition(toPosition)

  const achAccountAlias: TransferAccountInput = {
    alias: achAccountisTo ? toPosition.alias : fromPosition.alias,
    isTo: achAccountisTo,
  }

  const investmentAccountAlias: TransferAccountInput = {
    alias: investmentAccountisTo ? toPosition.alias : fromPosition.alias,
    isTo: investmentAccountisTo,
  }

  const isRetirementTransaction =
    isIRAAccountType(fromPosition.accountType as InvestmentAccountType) ||
    isIRAAccountType(toPosition.accountType as InvestmentAccountType) ||
    isInheritedIRAAccountType(fromPosition.accountType as InvestmentAccountType) ||
    isInheritedIRAAccountType(toPosition.accountType as InvestmentAccountType)

  const achAuthTermsArray = config.zendeskArticles.achAuthorization.split('/')
  const achAuthTermsId = `${achAuthTermsArray[achAuthTermsArray.length - 2]}/${achAuthTermsArray[achAuthTermsArray.length - 1]}`

  const disclosures = [
    {
      id: achAuthTermsId,
      accepted: true,
    },
  ]

  return {
    achAccountAlias,
    investmentAccountAlias,
    disclosures,
    money,
  }
}

export const buildInternalBankTransferPayload = ({
  fromPosition,
  toPosition,
  amount,
}: TBuildPayload): CreateBankInternalTransferInput => {
  return {
    amount: buildMoneyFromAmountField(amount),
    fromAlias: fromPosition.alias,
    toAlias: toPosition.alias,
  }
}

export const checkOrderType = ({
  fromFieldOption,
  toFieldOption,
}: {
  fromFieldOption: TransferListOption | undefined
  toFieldOption: TransferListOption | undefined
}): ETransferType => {
  if (!fromFieldOption?.value || !toFieldOption?.value) return ETransferType.NA

  const hasAchAccount = checkIfAchAccountPosition(fromFieldOption) || checkIfAchAccountPosition(toFieldOption)
  const hasInvestmentAccount =
    checkIfInvestmentAccountPosition(fromFieldOption) || checkIfInvestmentAccountPosition(toFieldOption)

  // InvestmentAch: Transfer between Investment account and external ACH account
  if (hasAchAccount && hasInvestmentAccount) return ETransferType.InvestmentAch
  // ACH: Transfer involving external bank account (no investment account)
  if (hasAchAccount) return ETransferType.ACH
  // BankInvestment: Transfer between EGW bank and investment account
  if (hasInvestmentAccount) return ETransferType.BankInvestment
  // BookTransfer: Internal transfer between EGW bank accounts
  return ETransferType.BookTransfer
}

export const buildTags = (data: CustomerDetails) => data.customer.tags

export const buildTransferValidationSchema = ({
  t,
  availableBalanceAmount,
  min100BrokerageDeposit,
}: {
  t: TFunction
  availableBalanceAmount: number | undefined
  min100BrokerageDeposit: boolean
}) => ({
  [TransferField.From]: yup.object().nonNullable().required(),
  [TransferField.To]: yup.object().nonNullable().required(),
  [TransferField.Amount]: yup
    .string()
    .when([TransferField.From], {
      // If From Value is an Evergreen Account
      is: (val: TransferListOption | undefined) => val?.display.isEvergreenAccount,
      // Then Ensure that the amount is not more than the available balance of that account
      then: (schema) =>
        schema
          .required()
          .test(
            TransferField.Amount,
            t('transfer.error.NSF_INLINE'),
            (item: string) => Number(availableBalanceAmount) >= removeCurrencySymbols(item),
          ),
      // If not just make sure the field is required
      otherwise: (schema) => schema.required(),
    })
    .when([TransferField.From], {
      // When From Account is IRA Account
      is: (val: TransferListOption | undefined) =>
        val?.accountType &&
        [InvestmentAccountType.ROTH_IRA, InvestmentAccountType.TRADITIONAL_IRA].includes(
          val.accountType as unknown as InvestmentAccountType,
        ),
      then: (schema) => {
        let message = '' as string
        return schema.required().test(
          TransferField.Amount,
          () => message,
          (item: string, formContext) => {
            const requestedAmount = (
              formContext.parent?.[TransferField.From]?.attributes as InvestmentAccountAttributes
            ).iraWithdrawal?.withdrawalAmount
            message =
              requestedAmount === undefined
                ? t('transfer.contact_wealth_advisor_withdrawal')
                : t('transfer.requested_withdrawal_amount', {
                    requestedAmount: buildBasicCurrency({
                      amount: requestedAmount,
                      currencyCode: DEFAULT_CURRENCY_CODE,
                    }),
                  })

            // If requested amount is not undefined check if amount is equal to IRA Withdrawal Requested Amount
            // Otherwise throw inline error (Message ternary above handles verbiage)
            return requestedAmount !== undefined ? removeCurrencySymbols(item) === requestedAmount : false
          },
        )
      },
      // If not just make sure the field is required
      otherwise: (schema) => schema.required(),
    })
    .when([TransferField.To], {
      is: (val: TransferListOption | undefined) => {
        if (typeof val?.alias === 'number' && val.alias !== -1) {
          return val.attributes && val.attributes.minimumDepositMet === false
        }
        return false
      },
      then: (schema: yup.StringSchema) => {
        let message = '' as string
        return schema.required().test(
          TransferField.Amount,
          () => message,
          (item: string, formContext) => {
            const minimumDepositAmount = formContext.parent?.[TransferField.To]?.attributes?.minimumDeposit
            const formattedCurrency = Currency.integerCents.onlyMinus(minimumDepositAmount)
            message = t('transfer.error.transer_min_not_met', { formattedCurrency })
            return removeCurrencySymbols(item) >= formContext.parent?.[TransferField.To]?.attributes?.minimumDeposit
          },
        )
      },
      // If not just make sure the field is required
      otherwise: (schema: yup.StringSchema) => schema.required(),
    })
    .when([TransferField.To], {
      is: (val: TransferListOption | undefined) =>
        val?.accountType &&
        [InvestmentAccountType.ROTH_IRA, InvestmentAccountType.TRADITIONAL_IRA].includes(
          val.accountType as unknown as InvestmentAccountType,
        ),
      then: (schema) => {
        let message = '' as string
        return schema.required().test(
          TransferField.Amount,
          () => message,
          (item: string, formContext) => {
            const annualContributionLimit = formContext.parent?.[TransferField.To]?.retirementContribution
              ?.annualContributionsLimit as number

            const remainingAnnualLimit = formContext.parent?.[TransferField.To]?.retirementContribution
              ?.remainingAnnualLimit as number

            // If the remaining limit is 0, then the user has exceeded the annual contribution limit
            if (remainingAnnualLimit === 0) {
              message = t('transfer.bank_investment_transfer_error.AnnualRetirementContributionExceededError', {
                annualContributionLimit: Currency.integerCents.onlyMinus(annualContributionLimit),
              })
              return false
            }
            // If the remaining limit is less than the amount, then the user has exceeded the annual contribution limit
            message = t('transfer.bank_investment_transfer_error.remainingLimitError', {
              remainingAnnualLimit: Currency.integerCents.onlyMinus(remainingAnnualLimit),
            })
            return removeCurrencySymbols(item) <= remainingAnnualLimit
          },
        )
      },
      // If not just make sure the field is required
      otherwise: (schema) => schema.required(),
    })
    .when([TransferField.To], {
      is: (val: TransferListOption | undefined) => val?.accountType && checkIfInvestmentAccount(val.accountType),
      then: (schema) =>
        schema
          .required()
          .test(
            TransferField.Amount,
            t('transfer.bank_investment_transfer_error.MINIMUM_DEPOSIT_NOT_MET'),
            (item: string) => removeCurrencySymbols(item) >= (min100BrokerageDeposit ? 100 : 0),
          ),
      // If not just make sure the field is required
      otherwise: (schema) => schema.required(),
    })
    // FE-943: Validate maximum transfer amount of $100,000 for all transfer types
    // This validation applies to Bank/Investment, ACH, Internal, and Investment ACH transfers
    .test(
      TransferField.Amount,
      t('transfer.bank_investment_transfer_error.investment_ach_transfer_max_amount'),
      (item: string | undefined) => {
        if (!item) return true
        const MAX_TRANSFER_AMOUNT = 100000 // $100,000
        return removeCurrencySymbols(item) <= MAX_TRANSFER_AMOUNT
      },
    ),
})

export const buildAchTransferLogPayload = (
  payload: CreateAchOrderInput,
  checkingAccountAlias: number | null | undefined,
) => {
  const event =
    payload.orderType === OrderType.COLLECTION_ORDER ? 'egw_fundAccountTransfer_submit' : 'user_transferFunds_click'

  return {
    event,
    payload: {
      clientId: Cookies.get(PARTY_ID_COOKIE),
      checkingAccountAlias,
      amount: payload.money,
      orderType: payload.orderType,
    },
  }
}

export const getCheckingAccountFromAllOptions = (
  allFieldOptions: TransferListOption[],
): TransferListOption | undefined => {
  const allChecking: TransferListOption[] = allFieldOptions
    .filter((each) => each.display.isEvergreenAccount)
    .filter((each) => each.accountType === BankAccountType.HIGH_YIELD_CHECKING)
  return allChecking[0]
}

export const getTransferSuccessType = (
  data: CreateBankInvestmentTransfer,
  variables?: CreateBankInvestmentTransferInput,
): TransferSuccessTypes => {
  // Money is moving from Bank to Investment Account
  const moneyMovementIsToInvestmentAccount = variables?.investmentAccountAlias.isTo

  // Market is either open or closing soon
  const marketOpen: boolean = data.marketStatus === MarketStatus.OPEN

  // Money is a Deposit to Investment Account || Withdraw from Investment Account
  const movement: BankInvestmentTransferType = moneyMovementIsToInvestmentAccount
    ? BankInvestmentTransferType.Deposit
    : BankInvestmentTransferType.Withdraw

  if (movement === BankInvestmentTransferType.Deposit) {
    // Deposit Market is Open
    if (marketOpen) {
      return TransferSuccessTypes.DepositMarketOpen
    }
    // Deposit Market is Closed
    return TransferSuccessTypes.DepositMarketClosed
  }
  // Withdraw Market is Open
  if (marketOpen) {
    return TransferSuccessTypes.WithdrawMarketOpen
  }
  // Withdraw Market is Closed
  return TransferSuccessTypes.WithdrawMarketClosed
}

// Helper function to build a transfer account option (simplified version without hidden logic)
export const buildTransferAccountOption = ({
  account,
  t,
}: {
  account: InvestmentAccountDetail
  t: TFunction<'features-transfer'>
}): TransferListOption => {
  const dictionary = TRANSFERABLE_ACCOUNTS_DICTIONARY_APEX_MIGRATION
  const accountType = getAccountTypeFromTypename(account.__typename)
  const isCompanionAccount = isCompanionAccountType(accountType)
  const subtitle = t(`transfer.investment_account_subtitles.${account.__typename}`)

  return {
    value: account.accountNumber,
    display: {
      name: account.accountName,
      subtitle,
      isEvergreenAccount: true,
    },
    accountTitle: account.accountTitle,
    alias: account.alias,
    accountType,
    transferableToAccounts: dictionary[accountType],
    isDisabled: account.status === InvestmentAccountStatus.CLOSED || isCompanionAccount,
    attributes: 'attributes' in account ? account.attributes : undefined,
    retirementContribution: 'retirementContribution' in account ? account.retirementContribution : undefined, // Roth and Traditional IRA Value Only
  }
}

// Build a transfer account option for an investment account FOR RECEIVE WIRE TRANSFER SPECIFICALLY
export const buildReceiveWireTransferAccounts = (
  accounts: InvestmentAccountDetail[] | undefined,
  t: TFunction<'features-transfer'>,
): TransferListOption[] => {
  if (!accounts) return []

  return accounts.map((account: InvestmentAccountDetail) => buildTransferAccountOption({ account, t }))
}
